import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  /** Right-align numeric columns; cells get tabular-nums automatically. */
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  /** Value used for sorting (defaults to `render` output when it is primitive). */
  sortValue?: (row: T) => string | number | Date | null | undefined;
  render: (row: T) => ReactNode;
  /** Extra classes for both header and cells (e.g. w-32, hidden md:table-cell). */
  className?: string;
  /** This column carries the row's link (see rowHref). Default: column 0. */
  isRowLink?: boolean;
}

export type DataTableSort = { key: string; dir: 'asc' | 'desc' } | null;

export interface DataTableProps<T> {
  columns: Array<DataTableColumn<T>>;
  data: T[];
  rowKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  /**
   * Destination for a row that represents a navigable record. The FIRST column's
   * content becomes a real `<a>` to it, which is what makes the row reachable by
   * Tab, openable with cmd/middle-click, and announced as a link — a
   * `cursor-pointer` `<tr>` with an onClick is none of those things. The rest of
   * the row still responds to `onRowClick` for convenience.
   */
  rowHref?: (row: T) => string;
  /**
   * Which column carries the row link (default: the first). A SELECTION
   * column has to sit before the name column, and wrapping a checkbox in the
   * row's anchor makes every tick a navigation — the exact interactive-inside-
   * anchor nesting the rowHref rule exists to prevent. The name column sets
   * `isRowLink` instead.
   */
  /** Accessible name for the row link (defaults to the first cell's text). */
  rowLabel?: (row: T) => string;
  /** Extra classes for one row — e.g. /invoices dims a draft so the money
   *  actually owed reads first. Presentation only; never hides a row. */
  rowClassName?: (row: T) => string | undefined;
  /** Rendered when data is empty (pass an <EmptyState/>). */
  empty?: ReactNode;
  /** Max height enables the sticky header + internal scroll. */
  maxHeight?: string;
  defaultSort?: { key: string; dir: 'asc' | 'desc' };
  /**
   * Controlled sort state (pair with onSortChange). When provided it wins over
   * internal state; columns without `sortValue` are assumed pre-sorted by the
   * parent (e.g. server-side ordering).
   */
  sort?: DataTableSort;
  /** Called with the next sort state when a sortable header is clicked. */
  onSortChange?: (sort: DataTableSort) => void;
  /** Compact density for dense financial tables (default true). */
  dense?: boolean;
  /**
   * Row padding. `compact` buys ~14px a row back for long triage lists where
   * how many rows you can see IS the feature; `dense` (default) is the normal
   * financial table; `roomy` is `dense={false}`.
   */
  density?: 'compact' | 'dense' | 'roomy';
  footer?: ReactNode;
  className?: string;
  /**
   * Group rows under a header row — /agreements groups by client. Groups are
   * ordered A→Z by `label`; the column sort still applies WITHIN each group,
   * so "worst margin first" still reads top-down inside every client.
   */
  group?: DataTableGroup<T>;
}

export interface DataTableGroup<T> {
  /** Rows with the same key share a header. */
  key: (row: T) => string;
  /** The header's text, and the A→Z order of the groups. */
  label: (row: T) => string;
}

type SortState = DataTableSort;

/** A value that has no place on the ramp — it must never outrank a real one. */
function isBlank(v: unknown): boolean {
  return v == null || (typeof v === 'number' && Number.isNaN(v));
}

function compareValues(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * The table's sort, as a pure function.
 *
 * Exported because two things other than the `<tbody>` need the exact same
 * order: a page that slices rows into pages before handing them over, and
 * **every CSV export** — a file whose rows are in a different order from the
 * screen that produced it is a file the reader has to re-sort to trust.
 * /invoices had already grown a private copy of this comparator with a
 * different null rule, so its page slice and the table's own order could
 * disagree about where the blanks went.
 */
export function sortRows<T>(
  data: T[],
  columns: Array<DataTableColumn<T>>,
  sort: DataTableSort,
): T[] {
  if (!sort) return data;
  const col = columns.find((c) => c.key === sort.key);
  if (!col?.sortValue) return data;
  const dir = sort.dir === 'asc' ? 1 : -1;
  // Nulls SINK, in both directions. A cell that prints "—" has no value on
  // the ramp, so ranking it as negative-infinity made `?sort=margin&dir=asc`
  // — "worst margin first", the whole reason to click it — open with the two
  // internal agreements above three hosting agreements at −606.6%, −178.2% and
  // −64.2%. A financial table that hides the worst row behind blanks is worse
  // than an unsorted one.
  return [...data].sort((a, b) => {
    const va = col.sortValue!(a);
    const vb = col.sortValue!(b);
    const ba = isBlank(va);
    const bb = isBlank(vb);
    if (ba || bb) return ba && bb ? 0 : ba ? 1 : -1;
    return dir * compareValues(va, vb);
  });
}

/**
 * Groups, A→Z by label, each keeping the order it was handed (the sort). Pure
 * and exported for the same reason `sortRows` is: a CSV of a grouped screen
 * must come out in the screen's order.
 */
export function groupRows<T>(rows: T[], group: DataTableGroup<T>): T[] {
  const order = new Map<string, number>();
  rows.forEach((r, i) => {
    if (!order.has(group.key(r))) order.set(group.key(r), i);
  });
  return [...rows].sort((a, b) => {
    const byLabel = group
      .label(a)
      .localeCompare(group.label(b), undefined, { numeric: true, sensitivity: 'base' });
    if (byLabel !== 0) return byLabel;
    // Two groups with one label stay apart; within a group, keep the sort.
    const byKey = order.get(group.key(a))! - order.get(group.key(b))!;
    return byKey !== 0 ? byKey : rows.indexOf(a) - rows.indexOf(b);
  });
}

/**
 * `"margin:desc"` ⇄ sort state, for keeping the sort in the URL alongside the
 * filters (see the "list filters live in the URL" rule). A sorted view is then
 * a link you can send someone, and its CSV export is reproducible.
 */
export function parseSortParam(value: string | null, fallback: DataTableSort = null): DataTableSort {
  if (!value) return fallback;
  const [key, dir] = value.split(':');
  return key && (dir === 'asc' || dir === 'desc') ? { key, dir } : fallback;
}

export function serializeSortParam(sort: DataTableSort): string | null {
  return sort ? `${sort.key}:${sort.dir}` : null;
}

export function DataTable<T>({
  columns,
  data,
  rowKey,
  onRowClick,
  rowHref,
  rowLabel,
  rowClassName,
  empty,
  maxHeight,
  defaultSort,
  sort: controlledSort,
  onSortChange,
  dense = true,
  density,
  footer,
  className,
  group,
}: DataTableProps<T>) {
  const [internalSort, setInternalSort] = useState<SortState>(defaultSort ?? null);
  const isControlled = controlledSort !== undefined;
  const sort = isControlled ? controlledSort : internalSort;

  const sorted = useMemo(() => {
    const bySort = sortRows(data, columns, sort);
    return group ? groupRows(bySort, group) : bySort;
  }, [data, sort, columns, group]);
  const groupCounts = useMemo(() => {
    const counts = new Map<string, number>();
    if (group) for (const r of data) counts.set(group.key(r), (counts.get(group.key(r)) ?? 0) + 1);
    return counts;
  }, [data, group]);

  const toggleSort = (col: DataTableColumn<T>) => {
    if (!col.sortable) return;
    const next: SortState =
      sort?.key === col.key
        ? { key: col.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
        : { key: col.key, dir: 'asc' };
    if (!isControlled) setInternalSort(next);
    onSortChange?.(next);
  };

  if (data.length === 0 && empty) {
    return <div className={className}>{empty}</div>;
  }

  const cellPad = {
    compact: 'px-3 py-1.5',
    dense: 'px-4 py-2.5',
    roomy: 'px-4 py-3.5',
  }[density ?? (dense ? 'dense' : 'roomy')];

  return (
    <div
      className={cn('relative overflow-x-auto', maxHeight && 'overflow-y-auto', className)}
      style={maxHeight ? { maxHeight } : undefined}
    >
      <table className="w-full border-collapse text-body-sm">
        <thead className="sticky top-0 z-10">
          <tr className="bg-grey-50 text-left">
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                aria-sort={
                  sort?.key === col.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                className={cn(
                  'border-b border-grey-200 text-caption font-bold uppercase tracking-wider text-grey-500 select-none',
                  cellPad,
                  col.align === 'right' && 'text-right',
                  col.align === 'center' && 'text-center',
                  col.className,
                )}
              >
                {col.sortable ? (
                  <button
                    type="button"
                    onClick={() => toggleSort(col)}
                    className={cn(
                      'inline-flex items-center gap-1 uppercase tracking-wider hover:text-secondary transition-colors',
                      col.align === 'right' && 'flex-row-reverse',
                    )}
                  >
                    {col.header}
                    {sort?.key === col.key ? (
                      sort.dir === 'asc' ? (
                        <ArrowUp className="h-3 w-3" aria-hidden />
                      ) : (
                        <ArrowDown className="h-3 w-3" aria-hidden />
                      )
                    ) : (
                      <ArrowUpDown className="h-3 w-3 opacity-40" aria-hidden />
                    )}
                  </button>
                ) : (
                  col.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white">
          {sorted.map((row, rowIndex) => {
            const href = rowHref?.(row);
            const groupKey = group?.key(row);
            const startsGroup =
              group !== undefined && (rowIndex === 0 || group.key(sorted[rowIndex - 1]) !== groupKey);
            return (
              <Fragment key={rowKey(row)}>
              {startsGroup && (
                <tr className="border-b border-grey-200 bg-grey-50">
                  <th
                    scope="colgroup"
                    colSpan={columns.length}
                    className={cn('text-left text-body-sm font-bold text-secondary', cellPad)}
                  >
                    {group!.label(row)}
                    <span className="ml-2 font-normal text-grey-500">{groupCounts.get(groupKey!)}</span>
                  </th>
                </tr>
              )}
              <tr
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'group border-b border-grey-100 last:border-b-0',
                  (onRowClick || href) && 'cursor-pointer transition-colors hover:bg-grey-50',
                  rowClassName?.(row),
                )}
              >
                {columns.map((col, i) => {
                  const linkIdx = Math.max(0, columns.findIndex((c) => c.isRowLink));
                  return (
                  <td
                    key={col.key}
                    className={cn(
                      'text-secondary-800',
                      cellPad,
                      col.align === 'right' && 'num-cell',
                      col.align === 'center' && 'text-center',
                      col.className,
                    )}
                  >
                    {i === linkIdx && href ? (
                      <Link
                        to={href}
                        aria-label={rowLabel?.(row)}
                        // Stop the row's own onClick from firing a second
                        // navigation on top of the link's.
                        onClick={(e) => e.stopPropagation()}
                        className="-m-1 block rounded-md p-1 no-underline outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                      >
                        {col.render(row)}
                      </Link>
                    ) : (
                      col.render(row)
                    )}
                  </td>
                  );
                })}
              </tr>
              </Fragment>
            );
          })}
        </tbody>
        {footer && <tfoot className="bg-grey-50 font-semibold">{footer}</tfoot>}
      </table>
    </div>
  );
}
